import { DollarSign, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

import { HubPlan } from "data/models";
import { roundDownOverageBudget } from "utils/calculations";
import { formatPrice } from "utils/format";

interface OverageConfigCardProps {
  selectedPlan: HubPlan | null;
  canConfigureOverage: boolean;
  overageInput: number;
  onChange: (value: number, rounded: number) => void;
}

export const OverageConfigCard = ({
  selectedPlan,
  canConfigureOverage,
  overageInput,
  onChange,
}: OverageConfigCardProps) => {
  const roundedBudget = selectedPlan
    ? roundDownOverageBudget(overageInput, {
        overageRate: selectedPlan.overage_cost_dollars!,
        planCredits: selectedPlan.credit_amount,
      })
    : 0;

  const showRoundingAlert =
    selectedPlan &&
    canConfigureOverage &&
    overageInput !== undefined &&
    overageInput !== roundedBudget;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overage</CardTitle>
        <CardDescription>Set a maximum spend limit</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-end gap-2">
          <Field className="w-60">
            <FieldLabel htmlFor="overage-limit-input">Overage Limit</FieldLabel>
            <InputGroup>
              <InputGroupAddon>
                <DollarSign />
              </InputGroupAddon>
              <InputGroupInput
                id="overage-limit-input"
                type="number"
                min={0}
                max={10000}
                disabled={!selectedPlan || !canConfigureOverage}
                value={overageInput}
                onChange={(e) => {
                  const value = Math.min(parseInt(e.target.value), 10000);
                  const rounded = selectedPlan
                    ? roundDownOverageBudget(value, {
                        overageRate: selectedPlan.overage_cost_dollars!,
                        planCredits: selectedPlan.credit_amount,
                      })
                    : 0;
                  onChange(value, rounded);
                }}
              />
              {selectedPlan && (
                <InputGroupAddon align="inline-end">
                  /{selectedPlan.cadence}
                </InputGroupAddon>
              )}
            </InputGroup>
          </Field>
        </div>
        {showRoundingAlert && (
          <Alert variant="info">
            <Info />
            <AlertDescription>
              The overage limit will automatically be rounded down to the
              nearest ${selectedPlan.overage_cost_dollars! * 1000} increment the
              equivalent of 1000 credits ({formatPrice(roundedBudget)}) when
              saved.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
